import { useEffect, useState, useCallback } from "react";
import { Plus, Loader2, ListChecks, Archive, ArchiveRestore, Pencil } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmptyState, LoadingState, PermissionDenied } from "@/components/states";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ChecklistTemplateEditor } from "@/components/checklists/ChecklistTemplateEditor";
import { usePermissions } from "@/hooks/usePermissions";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

const fdate = (iso) => { try { return format(new Date(iso), "d MMM yyyy"); } catch { return "—"; } };

export default function Checklists() {
  const perms = usePermissions();
  const canView = perms.has("view_checklist_templates");
  const canCreate = perms.has("create_checklist_templates");
  const canEdit = perms.has("edit_checklist_templates");
  const canPublish = perms.has("publish_checklist_templates");
  const canArchive = perms.has("archive_checklist_templates");
  const [tab, setTab] = useState("active");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [editorId, setEditorId] = useState(null);

  const load = useCallback(() => {
    if (!canView) { setLoading(false); return; }
    setLoading(true);
    apiFetch(`/checklists/templates?status=${tab}`)
      .then((r) => setItems(r.items || []))
      .catch((e) => toast.error("Could not load checklists", { description: e.message }))
      .finally(() => setLoading(false));
  }, [tab, canView]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!name.trim()) return toast.error("Please give the checklist a name.");
    setBusy(true);
    try {
      const t = await apiFetch("/checklists/templates", { method: "POST", body: JSON.stringify({ name: name.trim(), subject_type: "master_topic", gate_event: "master_topic.submit_for_review" }) });
      setCreating(false); setName(""); setEditorId(t.id); load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const toggleArchive = async (t) => {
    try { await apiFetch(`/checklists/templates/${t.id}/archive`, { method: "POST", body: JSON.stringify({ archived: !t.archived }) });
      toast.success(t.archived ? "Restored" : "Archived"); load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div data-testid="checklists-page">
      {!canView ? <PermissionDenied message="You don't have access to manage checklists. Ask an administrator if you need it." /> : (<>
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h2 className="font-heading text-xl font-semibold">Checklists</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">Reusable readiness criteria for Topics and future content workflows.</p>
        </div>
        {canCreate && <Button size="sm" className="gap-1.5" onClick={() => setCreating(true)} data-testid="checklist-new"><Plus className="h-4 w-4" /> Create Checklist</Button>}
      </div>

      <div className="mb-4 flex gap-1.5 border-b border-border/70">
        {[["active", "Active"], ["archived", "Archived"]].map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`checklists-tab-${v}`}
            className={cn("-mb-px rounded-t-lg px-3.5 py-2 text-sm font-medium transition-colors",
              tab === v ? "border-b-2 border-accent text-foreground" : "text-muted-foreground hover:text-foreground")}>{l}</button>
        ))}
      </div>

      {loading ? <LoadingState label="Loading checklists…" />
        : items.length === 0 ? (
          <EmptyState title={tab === "archived" ? "No archived checklists" : "No checklist templates yet"}
            message={tab === "archived" ? "Archived checklists will appear here." : "Create reusable readiness criteria for Topics and future content workflows."}
            action={tab === "active" && canCreate ? <Button size="sm" className="gap-1.5" onClick={() => setCreating(true)} data-testid="checklist-empty-new"><Plus className="h-4 w-4" /> Create Checklist Template</Button> : null} />
        ) : (
          <div className="grid gap-2.5" data-testid="checklists-list">
            {items.map((t) => (
              <div key={t.id} className="flex items-start justify-between gap-3 rounded-xl border border-border/70 bg-card p-4" data-testid={`checklist-row-${t.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <ListChecks className="h-4 w-4 shrink-0 text-accent" />
                    <span className="font-semibold text-foreground">{t.name}</span>
                    {t.active && t.published_version_number ? <span className="rounded-full bg-success/10 px-2 py-0.5 text-[11px] font-medium text-success">Active · v{t.published_version_number}</span>
                      : <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">{t.published_version_number ? "Inactive" : "Draft only"}</span>}
                    {t.has_draft && <span className="rounded-full bg-warning/10 px-2 py-0.5 text-[11px] font-medium text-warning">Unpublished draft</span>}
                  </div>
                  <p className="mt-1 text-[12px] text-muted-foreground">Topics · Submit for Review · {t.item_count || 0} items · Updated {fdate(t.updated_at)}</p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  {!t.archived && canEdit && <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setEditorId(t.id)} data-testid={`checklist-edit-${t.id}`}><Pencil className="h-3.5 w-3.5" /> Edit</Button>}
                  {canArchive && <Button size="sm" variant="ghost" className="gap-1.5" onClick={() => toggleArchive(t)} data-testid={`checklist-archive-${t.id}`}>
                    {t.archived ? <><ArchiveRestore className="h-3.5 w-3.5" /> Restore</> : <><Archive className="h-3.5 w-3.5" /> Archive</>}
                  </Button>}
                </div>
              </div>
            ))}
          </div>
        )}

      <Dialog open={creating} onOpenChange={(o) => { if (!o) { setCreating(false); setName(""); } }}>
        <DialogContent data-testid="checklist-create-dialog">
          <DialogHeader><DialogTitle>Create Checklist Template</DialogTitle></DialogHeader>
          <div className="space-y-2 py-1">
            <Label className="text-xs">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Topic Readiness" autoFocus data-testid="checklist-create-name"
              onKeyDown={(e) => e.key === "Enter" && create()} />
            <p className="text-[12px] text-muted-foreground">Applies to Topics, gating Submit for Review. You'll add items next.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreating(false); setName(""); }}>Cancel</Button>
            <Button onClick={create} disabled={busy} data-testid="checklist-create-confirm">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create & Add Items"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {editorId && <ChecklistTemplateEditor templateId={editorId} canPublish={canPublish} onClose={() => setEditorId(null)} onSaved={load} />}
      </>)}
    </div>
  );
}
